import { Fragment, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { ExclamationIcon } from "@heroicons/react/outline";
import { CheckoutConflictCode, CheckoutConflictResolution } from "../../../../graphql/types";
import { CheckoutCartConflict } from "../hooks/useCheckoutCart";

interface Props {
  /** The conflict returned by addCheckoutCartItem; null hides the modal. */
  conflict: CheckoutCartConflict | null;
  /** Re-call addCheckoutCartItem with `onConflict: resolution`. */
  onResolve: (resolution: CheckoutConflictResolution) => void;
  onCancel: () => void;
  busy?: boolean;
}

const TITLES: Record<CheckoutConflictCode, string> = {
  [CheckoutConflictCode.AliexpressMix]: "Replace your cart?",
  [CheckoutConflictCode.FreeDeliveryMix]: "Replace your cart?",
  [CheckoutConflictCode.InstantMix]: "Lose express delivery?",
};

const RESOLUTION_LABELS: Record<CheckoutConflictResolution, string> = {
  [CheckoutConflictResolution.ReplaceCart]: "Replace cart",
  [CheckoutConflictResolution.NoExpressDelivery]: "Add anyway",
};

/**
 * Generic "replace cart?" / "lose express delivery?" prompt driven entirely by
 * the server conflict (docs/server-checkout-migration.md §1.5.2). A conflict
 * means the item was NOT added; confirming re-calls the same mutation with
 * `onConflict` set to the chosen resolution. Cancel does nothing.
 */
const ConflictModal = ({ conflict, onResolve, onCancel, busy }: Props) => {
  const cancelButtonRef = useRef(null);

  return (
    <Transition.Root show={conflict !== null} as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-50 inset-0 overflow-y-auto md:w-1/2 mx-auto"
        initialFocus={cancelButtonRef}
        onClose={onCancel}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationIcon
                      className="h-6 w-6 text-yellow-600"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <Dialog.Title
                      as="h3"
                      className="text-lg leading-6 font-medium text-gray-900"
                    >
                      {conflict ? TITLES[conflict.code] : ""}
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        {conflict?.message}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                {conflict?.resolvableBy.map((resolution) => (
                  <button
                    key={resolution}
                    type="button"
                    disabled={busy}
                    onClick={() => onResolve(resolution)}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-buttonPrimary text-base font-medium text-white disabled:bg-gray-100 disabled:text-blue-500 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {RESOLUTION_LABELS[resolution]}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={busy}
                  onClick={onCancel}
                  ref={cancelButtonRef}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ConflictModal;
